import { GRID_SIZE } from "../constants/editor";
import { getSpritePreset } from "../data/sprites";
import { cloneGrid, cloneSceneLayers, createDefaultSceneLayers, createEmptyGrid, type CardDraft, type PixelGrid, type SceneLayer, type SceneLayerSlot } from "../shared/game";

const SLOT_ORDER: SceneLayerSlot[] = ["background", "character", "effect"];
export const SCENE_SCALE_STEPS = [0.5, 1, 2, 3, 4] as const;

type Bounds = {
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
  width: number;
  height: number;
};

export function getSceneLayers(card: CardDraft): SceneLayer[] {
  return cloneSceneLayers(card.sceneLayers ?? createDefaultSceneLayers());
}

export function getSceneLayer(card: CardDraft, slot: SceneLayerSlot): SceneLayer {
  return getSceneLayers(card).find((layer) => layer.slot === slot) ?? createDefaultSceneLayers().find((layer) => layer.slot === slot)!;
}

export function getSuggestedSceneLayer(slot: SceneLayerSlot, spriteId: string | null): SceneLayer {
  const layer = createDefaultSceneLayers().find((entry) => entry.slot === slot)!;
  if (!spriteId) {
    return layer;
  }

  const sprite = getSpritePreset(spriteId);
  const bounds = sprite ? getGridBounds(sprite.grid) : null;
  if (!bounds) {
    return { ...layer, spriteId };
  }

  const offsetX = Math.round((GRID_SIZE - bounds.width) / 2);
  const offsetY = slot === "character"
    ? Math.round(GRID_SIZE - bounds.height - 1)
    : Math.round((GRID_SIZE - bounds.height) / 2);

  return {
    ...layer,
    spriteId,
    offsetX,
    offsetY,
  };
}

export function getNextSceneScale(current: number, delta: number): number {
  const index = SCENE_SCALE_STEPS.findIndex((step) => step === current);
  const safeIndex = index >= 0 ? index : 1;
  const nextIndex = Math.max(0, Math.min(SCENE_SCALE_STEPS.length - 1, safeIndex + delta));
  return SCENE_SCALE_STEPS[nextIndex];
}

export function composeCardGrid(card: CardDraft): PixelGrid {
  const composed = createEmptyGrid();
  for (const slot of SLOT_ORDER) {
    const layer = getSceneLayer(card, slot);
    if (!layer.visible || !layer.spriteId) {
      continue;
    }

    const sprite = getSpritePreset(layer.spriteId);
    if (!sprite) {
      continue;
    }

    stampSpriteGrid(composed, sprite.grid, layer);
  }

  const paintGrid = cloneGrid(card.grid);
  for (let row = 0; row < GRID_SIZE; row += 1) {
    for (let col = 0; col < GRID_SIZE; col += 1) {
      if (paintGrid[row][col]) {
        composed[row][col] = paintGrid[row][col];
      }
    }
  }

  return composed;
}

function stampSpriteGrid(target: PixelGrid, source: PixelGrid, layer: SceneLayer): void {
  const bounds = getGridBounds(source);
  if (!bounds) {
    return;
  }

  if (layer.scale < 1) {
    stampDownscaledSpriteGrid(target, source, bounds, layer);
    return;
  }

  for (let row = bounds.minRow; row <= bounds.maxRow; row += 1) {
    for (let col = bounds.minCol; col <= bounds.maxCol; col += 1) {
      const color = source[row][col];
      if (!color) {
        continue;
      }

      const targetRow = layer.offsetY + (row - bounds.minRow) * layer.scale;
      const targetCol = layer.offsetX + (col - bounds.minCol) * layer.scale;

      for (let dy = 0; dy < layer.scale; dy += 1) {
        for (let dx = 0; dx < layer.scale; dx += 1) {
          const y = targetRow + dy;
          const x = targetCol + dx;
          if (y >= 0 && y < GRID_SIZE && x >= 0 && x < GRID_SIZE) {
            target[y][x] = color;
          }
        }
      }
    }
  }
}

function stampDownscaledSpriteGrid(target: PixelGrid, source: PixelGrid, bounds: Bounds, layer: SceneLayer): void {
  for (let row = bounds.minRow; row <= bounds.maxRow; row += 1) {
    for (let col = bounds.minCol; col <= bounds.maxCol; col += 1) {
      const color = source[row][col];
      if (!color) {
        continue;
      }

      const relativeRow = row - bounds.minRow;
      const relativeCol = col - bounds.minCol;
      const y = layer.offsetY + Math.floor(relativeRow * layer.scale);
      const x = layer.offsetX + Math.floor(relativeCol * layer.scale);

      if (y >= 0 && y < GRID_SIZE && x >= 0 && x < GRID_SIZE) {
        target[y][x] = color;
      }
    }
  }
}

function getGridBounds(grid: PixelGrid): Bounds | null {
  let minRow = GRID_SIZE;
  let maxRow = -1;
  let minCol = GRID_SIZE;
  let maxCol = -1;

  for (let row = 0; row < GRID_SIZE; row += 1) {
    for (let col = 0; col < GRID_SIZE; col += 1) {
      if (!grid[row][col]) {
        continue;
      }
      minRow = Math.min(minRow, row);
      maxRow = Math.max(maxRow, row);
      minCol = Math.min(minCol, col);
      maxCol = Math.max(maxCol, col);
    }
  }

  if (maxRow === -1 || maxCol === -1) {
    return null;
  }

  return {
    minRow,
    maxRow,
    minCol,
    maxCol,
    width: maxCol - minCol + 1,
    height: maxRow - minRow + 1,
  };
}
